// The instruction the app sends on the user's behalf when they open a brand-new session, plus the label the
// chat shows in its place.
//
// Pure and standalone, like ./autoAnalysePrompt and ./autoMapPrompt — next to the panel rather than in
// `utils/index.js` (which Vitest cannot parse — see "Vitest imports from src/utils" in frontend/CLAUDE.md),
// and taking no hooks, so the wording can be asserted directly.
//
// ## Why the app writes this at all
//
// The greeting used to be the literal string "hello", sent as if the user had typed it, and what came back
// was whatever the model made of that. There was nothing in the system prompt about a first turn — and what
// IS in it argues against a welcome: "**No filler.** Do not add introductory paragraphs, restate what the
// tool returned, or narrate each step." So the one turn that should be an introduction was the one turn
// instructed not to introduce anything, and the result varied run to run.
//
// ## Why it forbids tools
//
// This is the first thing a user waits for, and there is nothing to look at yet. `get_task_template` would
// turn a two-line hello into a Java round trip; profiling has no file to profile. Both also make the
// welcome's content depend on a call that can fail.

/**
 * @param {object} args
 * @param {string} args.target  AiIngestTarget name, when the session carries one.
 * @returns {{text: string, display: string}} `text` goes to the model; `display` is shown instead.
 */
export function buildGreetingPrompt({ target = "" } = {}) {
  const destination = target
    ? `the ${target} data task`
    : "a carbon data task — you do not know which part of it yet";

  return {
    display: "Starting a new session…",
    text: [
      "I have just opened a new session. I have not uploaded anything yet.",
      "",
      "Open the conversation: introduce yourself in two or three short lines — what you do here, and that",
      `you will be mapping my data into ${destination}. Then ask me for my file, and tell me I can drag a`,
      "CSV or Excel file onto the panel on the right, or click it to browse.",
      "",
      // Named explicitly, because the rule it overrides is real and a future reader of system.py should be
      // able to find out why one turn ignores it.
      'This turn is the one exception to "No filler. Do not add introductory paragraphs" in your',
      "instructions: an introduction is the entire purpose of it. Keep it warm and short.",
      "",
      "Do NOT call any tools — there is no data to look at and nothing to read from the destination until I",
      "have sent you something. Do NOT ask me anything except for the file.",
    ].join("\n"),
  };
}

export default buildGreetingPrompt;
